import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Icon, IconName } from 'components/ui/Icon';
import styles from './Toast.module.scss';

export interface IToastContentProps {
  title: string;
  body?: string;
  iconName?: IconName;
  iconClassName?: string;
  /** Injected by the RSuite toaster; closes this toast. */
  onClose?: () => void;
  /**
   * Auto-dismiss delay in ms. Passed by `showToast` (and also injected by the
   * RSuite toaster from `toaster.push(..., { duration })`). Custom content
   * must run the timer itself — RSuite only auto-closes Message/Notification.
   * `0` = stay open until dismissed.
   */
  duration?: number;
  /** Injected by the RSuite toaster (enter/exit transition classes). */
  className?: string;
  style?: React.CSSProperties;
}

const ToastContent = React.forwardRef<HTMLDivElement, IToastContentProps>(
  (
    {
      title,
      body,
      iconName = 'check-circle',
      iconClassName,
      onClose,
      duration = 4000,
      className,
      style,
    },
    ref,
  ) => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The toaster passes a new onClose each render; keep the latest in a ref
    // so re-renders (e.g. another toast pushed) don't restart the timer.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const clearTimer = useCallback(() => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }, []);

    const startTimer = useCallback(() => {
      clearTimer();
      if (typeof duration === 'number' && duration > 0) {
        timerRef.current = setTimeout(() => {
          onCloseRef.current?.();
        }, duration);
      }
    }, [clearTimer, duration]);

    useEffect(() => {
      startTimer();
      return clearTimer;
    }, [startTimer, clearTimer]);

    return (
      <div
        ref={ref}
        style={style}
        className={clsx(styles.toast, className)}
        role="status"
        aria-live="polite"
        // Pause while hovered so people can finish reading.
        onMouseEnter={clearTimer}
        onMouseLeave={startTimer}
      >
        <Icon
          name={iconName}
          size={24}
          className={iconClassName ?? styles.successIcon}
        />
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          {body ? <p className={styles.body}>{body}</p> : null}
        </div>
        {onClose ? (
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Dismiss notification"
          >
            <Icon name="x" size={16} />
          </button>
        ) : null}
      </div>
    );
  },
);

ToastContent.displayName = 'ToastContent';

export default ToastContent;
